import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { useOutletContext } from 'react-router';
import { useLazyQuery } from '@apollo/client';

import TmntContext from '../../context/tmnt';
import GET_RETURN_METHOD from '../../gql/return-methods.gql';
import Loading from '../../components/loading/loading';
import ErrorDisplay from '../../components/error-display/error-display';
import { useGetDigitalDataFlagValue } from '../../utils/digital-data-flag';

import ShippingMethods from '../../components/shipping-methods/shipping-methods';
import mapProductReturnDetail from '../../utils/map-product-return-detail';
import useGeocoder from '../../hooks/useGeocoder';

export default function ExchangeShippingMethodView() {
  const viewport = useRef();
  const tmntContext = useContext(TmntContext);
  const [areSelectedItemsHazmat, setAreSelectedItemsHazmat] = useState(undefined);

  const {
    setPageTitle,
    orderDetail,
    setReady,
    selectedShippingMethod,
    setSelectedShippingMethod,
    carriers,
    setCarriers,
    selectedProducts,
    selectedProductComments,
    setCurrentPostalCode,
    mapApiKey,
  } = useOutletContext();

  const {
    country,
    postalCode,
    state,
  } = orderDetail.originalOrder.orderDetails.shippingAddress;

  const fulDre20FedexDropOff = !!useGetDigitalDataFlagValue('ful-dre-2-0-fedex-drop-off', false);
  const enableFedExQR = !!useGetDigitalDataFlagValue('ful-dre-2-0-fedex-qrcode', false);
  const geocoder = useGeocoder({ apiKey: mapApiKey });

  const [getReturnMethod, { loading, error, data }] = useLazyQuery(GET_RETURN_METHOD);

  const onCarrierSelected = useCallback((carrier) => {
    setSelectedShippingMethod(carrier);
    setCurrentPostalCode(carrier.dropoffLocationsResponse?.postalCode);
  }, [setSelectedShippingMethod, setCurrentPostalCode]);

  useEffect(
    () => {
      if (!data?.returnMethods?.length) return;
      if (!(selectedShippingMethod && Object.keys(selectedShippingMethod).length > 0)) {
        setSelectedShippingMethod(data.returnMethods[0]);
      }

      // FUL-981: If the selectedShippingMethod is already set, we should not set the value
      setSelectedShippingMethod(
        (currentSelectedShippingMethod) => (
          [currentSelectedShippingMethod, selectedShippingMethod].find(Boolean)
        ),
      );
    },
    [
      data,
      selectedShippingMethod,
      setSelectedShippingMethod,
    ],
  );

  // FUL-931: If the selected shipping method is not in the list of carriers, set the first carrier
  // this can happen when a customer changes products which would yield a different set of carriers
  // e.g. a hazmat item is added or removed
  useEffect(() => {
    if (!selectedShippingMethod) return;
    if (!carriers?.length) return;
    if (!carriers.find((carrier) => carrier.type === selectedShippingMethod.type)) {
      setSelectedShippingMethod(carriers[0]);
    }
  }, [carriers, selectedShippingMethod]);

  useEffect(() => setPageTitle('DRE_WHERE_WOULD_YOU_LIKE_TO_RETURN_YOUR_ITEMS'), []);
  useEffect(() => setReady(!!selectedShippingMethod), [selectedShippingMethod]);

  // only set the carriers once
  useEffect(() => {
    if (!data?.returnMethods) return;
    // don't reset the carriers if they are already set
    if (carriers) return;
    setCarriers(() => data.returnMethods);
  }, [data]);

  // update the carriers when the returnMethods changes
  useEffect(() => {
    setCarriers((currentCarriers) => {
      if (!currentCarriers) return null;
      const index = currentCarriers.findIndex(
        (carrier) => carrier.type === selectedShippingMethod?.type,
      );
      const copy = [...currentCarriers];
      copy.splice(index, 1, selectedShippingMethod);
      return copy.filter((v) => !!v);
    });
  }, [selectedShippingMethod]);

  useEffect(() => {
    if (selectedProducts) {
      const tempAreItemsHazmat = selectedProducts.some((product) => product.isHazmat);

      // NOTE: planning to refactor this logic in exchange.jsx at some point
      const returnItems = selectedProducts.map((product, idx) => {
        const comments = selectedProductComments[product.orderLineKey];
        return mapProductReturnDetail({ product, comments, seq: idx });
      });

      setAreSelectedItemsHazmat(tempAreItemsHazmat);

      async function executeGetReturnMethod() {
        let lat;
        let lng;
        try {
          const geolocation = await geocoder.getAddressGeolocation(postalCode);
          lat = geolocation?.latlng?.[0]?.toString();
          lng = geolocation?.latlng?.[1]?.toString();
        } finally {
          getReturnMethod({
            variables: {
              dropoffLocationsInput: {
                country,
                postalCode,
                state,
                // NOTE: the server will not use this flag anymore soon enough
                dropoffEnabled: fulDre20FedexDropOff,
                // NOTE: the server will not use this flag anymore soon enough
                qrcodeEnabled: enableFedExQR,
                // NOTE: the server will soon enough just use the returnItems to determine hazmat
                hasHazmatItem: tempAreItemsHazmat,
                returnItems,
                lat,
                lng,
              },
            },
          });
        }
      }

      executeGetReturnMethod();
    }
  }, [
    selectedProducts,
    selectedProductComments,
    postalCode,
    country,
    enableFedExQR,
    state,
  ]);

  if (loading) return <Loading />;
  if (error) return <ErrorDisplay apolloError={error} />;

  return (
    <div className="exchange-location-selection-view" ref={viewport}>
      {areSelectedItemsHazmat && (
        <div className="hazmat-message" data-testid="hazmat-message">
          {tmntContext?.DRE_PCITEM_RETURN?.value}
        </div>
      )}
      <div>
        {Array.isArray(carriers) && carriers.map((carrier) => (
          <ShippingMethods
            key={carrier.type}
            selectedShippingMethod={selectedShippingMethod}
            setSelectedShippingMethod={onCarrierSelected}
            carrier={carrier}
            currentPostalCode={carrier.dropoffLocationsResponse?.postalCode}
          />
        ))}
      </div>
    </div>
  );
}
